import { getWhitelistedQuery, slugifyParam } from 'utils/url';
import SortOption from './models/SortOption';
import config from 'config';

// TODO this should be taken from configuration

const validTagId = (tagId) => tagId && tagId !== config.ROOT_SHOP_TAG_ID;

function safeTags(str) {
  return str === undefined ?
    str :
    str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/'/g, '&apos;')
      .replace(/"/g, '&quot;');
}

const buildPathname = (basePath, tags) => {
  const tagIds = tags ? decodeURIComponent(tags).split('|').filter(validTagId) : [];
  return tagIds.reduce((path, tagId) => `${path}/${tagId}`, basePath); // unable to get names from params
};

function translateCluster(cluster) {
  if (!cluster) {
    return undefined;
  }

  return decodeURIComponent(cluster).split('sub')
    .filter(validTagId)
    .map(tagId => (tagId === '-1' ? 'other' : tagId))
    .join(',');
}

function translateFilters(filters) {
  if (!filters) {
    return undefined;
  }

  return decodeURIComponent(filters).split('|')
    .filter(validTagId)
    .join(',');
}

function translatePriceFilters(priceFilters) {
  if (!priceFilters) {
    return undefined;
  }

  return priceFilters.replace('|', ',');
}

function translateSortBy(sortBy) {
  if (!sortBy || SortOption[sortBy.toUpperCase()] === undefined) {
    return undefined;
  }

  return typeof sortBy === 'string' ? sortBy.toUpperCase() : undefined;
}

function deduplicateEntry(entry) {
  if (Array.isArray(entry)) {
    return entry[0];
  }
  return entry;
}

export const translateSearch = (basePath, location) => {
  const whitelistedQuery = getWhitelistedQuery(location.query);
  const { tags, entry, showAllTopFavourites, cluster, filters, price, sortBy, hideOOS } = location.query;

  return {
    pathname: buildPathname(basePath, tags),
    query: {
      ...whitelistedQuery,
      entry: deduplicateEntry(entry),
      showAllFavourites: showAllTopFavourites,
      clusterPath: translateCluster(cluster),
      customFilters: translateFilters(filters),
      price: translatePriceFilters(price),
      sort: translateSortBy(sortBy),
      hideOOS,
    },
    state: {
      isCatalogueRedirect: true,
    },
  };
};

const searchPathReplace = (basePath) => (nextState, replace) => {
  replace(translateSearch(basePath, nextState.location));
};

export const translateBrowse = (basePath, location) => {
  const whitelistedQuery = getWhitelistedQuery(location.query);
  const { filters, price, sortBy, hideOOS } = location.query;
  const tags = safeTags(location.query.tags);
  return {
    pathname: buildPathname(basePath, tags),
    query: {
      ...whitelistedQuery,
      customFilters: translateFilters(filters),
      price: translatePriceFilters(price),
      sort: translateSortBy(sortBy),
      hideOOS,
    },
    state: {
      isCatalogueRedirect: true,
    },
  };
};

const browsePathReplace = (basePath) => (nextState, replace) => {
  replace(translateBrowse(basePath, nextState.location));
};

export const translatePromotion = (offerName, offerId, location) => {
  const whitelistedQuery = getWhitelistedQuery(location.query);

  return {
    pathname: `/offers/${slugifyParam(offerName, offerId)}`,
    query: whitelistedQuery,
    state: {
      isCatalogueRedirect: true,
    },
  };
};

const promotionPathReplace = (offerName, offerId) => (nextState, replace) => {
  replace(translatePromotion(offerName, offerId, nextState.location));
};

export default {
  childRoutes: [
    {
      path: 'getCategories(.do)(.go)',
      onEnterHandlers: [
        browsePathReplace('/browse'),
      ],
    },
    {
      path: 'category/(:tagNames)/:tagIds',
      onEnterHandlers: [
        (nextState, replace) => {
          const { tagIds } = nextState.params;
          const tagIdsArr = tagIds ? tagIds.split('-').filter(validTagId) : [];
          const pathname = ['/browse', ...tagIdsArr].join('/');

          replace({
            pathname,
            query: getWhitelistedQuery(nextState.location.query),
            state: {
              isCatalogueRedirect: true,
            },
          });
        },
      ],
    },
    {
      path: 'getSeasonalCategories(.do)(.go)',
      onEnterHandlers: [
        browsePathReplace('/browse'),
      ],
    },
    // {
    //   path: 'displayPrevPurchases(.do)(.go)',
    //   onEnter: tagsPathReplace('/favourites'),
    // },
    {
      path: 'getOffers(.do)(.go)',
      onEnterHandlers: [
        browsePathReplace('/on-offer'),
      ],
    },
    {
      path: 'getOfferProducts(.do)(.go)',
      onEnterHandlers: [
        browsePathReplace('/on-offer'),
      ],
    },
    {
      path: 'getSearchProducts(.do)(.go)',
      onEnterHandlers: [
        searchPathReplace('/search'),
      ],
    },
    {
      path: 'promotion/:offerName/:offerId',
      onEnterHandlers: [
        (nextState, replace) => {
          const { offerName, offerId } = nextState.params;
          promotionPathReplace(offerName, offerId)(nextState, replace);
        },
      ],
    },
    {
      path: 'displayOfferDetails(.do)(.go)',
      onEnterHandlers: [
        (nextState, replace) => {
          const offerName = null; // unable to get from query params
          const offerId = nextState.location.query.promoId;
          promotionPathReplace(offerName, offerId)(nextState, replace);
        },
      ],
    },
    // TODO when uncommented - update replace calls to forward query parameters - see WEBSHOP-22036
    // {
    //   path: 'bundles',
    //   onEnter: (nextState, replace) => replace('/bundles'),
    // },
    // {
    //   path: 'bundle/:bundleName/:bundleId',
    //   onEnter: (nextState, replace) => {
    //     const { bundleName, bundleId } = nextState.params;
    //     replace(`/bundles/${slugifyParam(bundleName, bundleId)}`);
    //   },
    // },
    // {
    //   path: 'displayBundles(.do)(.go)',
    //   onEnter: (nextState, replace) => {
    //     const bundleName = null; // unable to get from query params
    //     const bundleId = nextState.location.query.promoId;
    //     if (bundleId) {
    //       replace(`/bundles/${slugifyParam(bundleName, bundleId)}`);
    //     } else {
    //       // same path can be also used to display the list of bundles
    //       const isMealDeal = !!nextState.location.query.mealDealBundles;
    //       replace(isMealDeal ? '/meal-deals' : '/bundles');
    //     }
    //   },
    // },
    // {
    //   path: 'combo/:comboName/:comboId',
    //   onEnter: (nextState, replace) => {
    //     const { comboName, comboId } = nextState.params;
    //     replace(`/combo/${slugifyParam(comboName, comboId)}`);
    //   },
    // },
    // {
    //   path: 'displayComboDeal(.do)(.go)',
    //   onEnter: (nextState, replace) => {
    //     const comboName = null; // unable to get from query params
    //     const comboId = nextState.location.query.promoId;
    //     replace(`/combo/${slugifyParam(comboName, comboId)}`);
    //   },
    // },
  ],
};
